import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { fetchPlanReviewJustifications, savePlanReviewJustification } from '../api'
import { isPlanReviewJustificationValid } from '../lib/isPlanReviewJustificationValid'
import { buildPlanReviewJustificationParams } from '../lib/planReviewJustificationParams'

import {
  IPlanReviewJustificationRecord,
  IPlanReviewJustificationTarget,
  TPlanReviewJustificationRecords,
  TPlanReviewJustifications,
  TPlanReviewOffering,
} from './types'

interface IUsePlanReviewJustificationsParams {
  enabled: boolean
  partnerId: string
  fYear: number
  programType: TPlanReviewOffering
  isGlobal: boolean
  userEmail: string
  userFullName: string
}

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback

export const usePlanReviewJustifications = ({
  enabled,
  partnerId,
  fYear,
  programType,
  isGlobal,
  userEmail,
  userFullName,
}: IUsePlanReviewJustificationsParams) => {
  const [records, setRecords] = useState<TPlanReviewJustificationRecords>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const loadRequestIdRef = useRef(0)

  const fetchRecords = useCallback(
    () => fetchPlanReviewJustifications({ partnerId, fYear, programType, isGlobal }),
    [fYear, isGlobal, partnerId, programType]
  )

  const load = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current

    if (!enabled || !partnerId || !fYear || programType === ProgramTypes.None) {
      setRecords({})
      setLoadError(null)
      setSaveError(null)
      setIsLoading(false)
      return true
    }

    setRecords({})
    setIsLoading(true)
    setLoadError(null)

    try {
      const nextRecords = await fetchRecords()

      if (requestId === loadRequestIdRef.current) {
        setRecords(nextRecords)
      }
      return true
    } catch (error) {
      if (requestId === loadRequestIdRef.current) {
        setRecords({})
        setLoadError(getErrorMessage(error, 'Failed to load saved justifications.'))
      }
      return false
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setIsLoading(false)
      }
    }
  }, [enabled, fYear, fetchRecords, partnerId, programType])

  useEffect(() => {
    void load()
  }, [load])

  const save = useCallback(
    async (target: IPlanReviewJustificationTarget, value: string) => {
      if (!enabled || !partnerId || isSaving || !isPlanReviewJustificationValid(value)) {
        return false
      }

      const comment = value.trim()
      const params = buildPlanReviewJustificationParams({
        section: target.key,
        fYear,
        programType,
        isGlobal,
        userEmail,
        userFullName,
      })
      const currentRecord = records[target.key]

      setIsSaving(true)
      setSaveError(null)

      try {
        const savedRow = await savePlanReviewJustification({
          entryId: currentRecord?.entryId ?? 0,
          partnerId,
          params,
          comment,
        })
        const nextRecord: IPlanReviewJustificationRecord = {
          entryId: Number(savedRow?.EntryID ?? savedRow?.EntryId ?? currentRecord?.entryId ?? 0),
          partnerId,
          params,
          comment,
        }

        setRecords((current) => ({ ...current, [target.key]: nextRecord }))

        try {
          const refreshedRecords = await fetchRecords()
          setRecords(refreshedRecords)
          setLoadError(null)
        } catch (error) {
          setLoadError(getErrorMessage(error, 'Failed to reload saved justifications.'))
        }

        return true
      } catch (error) {
        setSaveError(getErrorMessage(error, 'Failed to save justification.'))
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [
      fYear,
      fetchRecords,
      enabled,
      isGlobal,
      isSaving,
      partnerId,
      programType,
      records,
      userEmail,
      userFullName,
    ]
  )

  const justifications = useMemo(
    () =>
      Object.values(records).reduce<TPlanReviewJustifications>((values, record) => {
        if (record) {
          values[record.params.PlanQualitySectionKey] = record.comment
        }

        return values
      }, {}),
    [records]
  )

  return {
    justifications,
    isLoading,
    isSaving,
    loadError,
    saveError,
    reload: load,
    save,
  }
}
